import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useImpersonation } from '../../hooks/useImpersonation';
import { RootState } from '../../store';
import { isElloPaymentLinkContext, NavigationConfig } from '../../utils/navigation-constants';
import CustomButton from '../button/ButtonComponent';
import { colors } from '../theme/theme';
import { BrandLogo } from '../ui-components/BrandLogo';

interface ImpersonationAttentionModalProps {
    open?: boolean;
    onClose?: () => void;
}

/**
 * Component to display attention message for super user impersonation
 * Informs user about 60-minute session timeout
 */
export const ImpersonationAttentionModal: React.FC<ImpersonationAttentionModalProps> = () => {
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const { handleAttentionModalClose } = useImpersonation();
    const { pathname, search } = useLocation();

    const isOpen = impersonation?.showAttentionModal;
    const isSelfRegistrationRoute = pathname === NavigationConfig.SelfRegistration;
    const isLoginRoute = pathname === NavigationConfig.Login;
    const hasValidToken = !!localStorage.getItem('authToken');

    // Don't show modal on login page if user has no valid tokens
    if (
        !isOpen ||
        isElloPaymentLinkContext(pathname, search) ||
        isSelfRegistrationRoute ||
        (isLoginRoute && !hasValidToken)
    ) {
        return null;
    }

    const handleOkClick = () => {
        // Call the hook's handler which starts countdown and navigates to contracts
        handleAttentionModalClose();
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100vh',
                zIndex: 9999,
                backgroundColor: colors.bluePreset[110],
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 3,
            }}
        >
            {/* Main Content Card */}
            <Stack
                className="loginCard"
                sx={{
                    maxWidth: '550px',
                    width: '100%',
                    backgroundColor: '#fff',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    padding: 1,
                }}
            >
                {/* Logo Section */}
                <Stack sx={{ my: 3 }}>
                    <BrandLogo
                        alt="Brand logo"
                        style={{ height: '100%', width: '100%', maxHeight: 60, maxWidth: 188 }}
                    />
                </Stack>

                {/* Content Section */}
                <Stack
                    sx={{
                        overflow: 'auto',
                        width: '100%',
                        padding: 3,
                        gap: 3,
                        textAlign: 'center',
                    }}
                >
                    {/* Attention Title */}
                    <Typography variant="h5" className="mainTitle">
                        Attention
                    </Typography>

                    {/* Message */}
                    <Typography
                        variant="body1"
                        color="textPrimary"
                        sx={{
                            lineHeight: 1.8,
                            textAlign: 'center',
                        }}
                    >
                        Your Super User login will remain active for <strong>60 minutes</strong>. After this period, you
                        will be automatically logged out for security reasons.
                    </Typography>

                    {/* OK Button */}
                    <Stack sx={{ alignItems: 'center', mt: 2 }}>
                        <CustomButton
                            variant="contained"
                            onClick={handleOkClick}
                            sx={{
                                width: 'fit-content',
                                minWidth: 120,
                                padding: '10px 32px',
                                backgroundColor: colors.errorPreset[10],
                                '&:hover': {
                                    backgroundColor: colors.errorPreset[10],
                                },
                            }}
                        >
                            OK
                        </CustomButton>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
};
